import { useCallback, useState, type FormEvent } from 'react';
import { ApiError, type ApiErrorCode } from '../api/client';
import type { SignupInput } from '../api/auth';
import { useT, type TFn } from '../i18n';
import { Turnstile } from './Turnstile';
import { CONSENT_VERSION, PRIVACY_URL, TERMS_URL } from '../consent';

interface Props {
  onSubmit: (input: SignupInput) => Promise<void>;
  // ANX-055: если задан — рендерится Turnstile-виджет, submit заблокирован
  // пока пользователь не пройдёт captcha. Если пуст — старое поведение.
  turnstileSiteKey?: string;
}

function errorMessage(e: unknown, t: TFn): string {
  if (e instanceof ApiError) {
    const map: Partial<Record<ApiErrorCode, string>> = {
      invalid_payload: t('signup.error.invalidPayload'),
      rate_limited: t('signup.error.rateLimited'),
      network: t('signup.error.network'),
      captcha_required: t('signup.error.captchaRequired'),
      captcha_failed: t('signup.error.captchaFailed'),
      captcha_unavailable: t('signup.error.captchaUnavailable'),
      consent_required: t('signup.error.consentRequired'),
    };
    // tenant_slug_taken приходит как нестандартный код — обрабатываем по message
    if (e.message.toLowerCase().includes('slug')) return t('signup.error.slugTaken');
    return map[e.code] ?? `${t('common.errorPrefix')}: ${e.message}`;
  }
  return t('common.unknownError');
}

export function SignupForm({ onSubmit, turnstileSiteKey }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const turnstileEnabled = Boolean(turnstileSiteKey);
  const onTurnstileToken = useCallback((token: string | null) => {
    setTurnstileToken(token);
  }, []);

  const canSubmit =
    !pending &&
    email.trim() !== '' &&
    password.length >= 8 &&
    tenantName.trim() !== '' &&
    consent &&
    (!turnstileEnabled || turnstileToken !== null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setPending(true);
    try {
      await onSubmit({
        email: email.trim().toLowerCase(),
        password,
        tenantName: tenantName.trim(),
        consentVersion: CONSENT_VERSION,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full max-w-sm flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('signup.email')}</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-md border border-slate-200 px-3 py-2 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('signup.password')}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          className="rounded-md border border-slate-200 px-3 py-2 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        />
        <span className="text-xs text-slate-500">{t('signup.passwordHint')}</span>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('signup.tenantName')}</span>
        <input
          type="text"
          autoComplete="organization"
          value={tenantName}
          onChange={(e) => setTenantName(e.target.value)}
          className="rounded-md border border-slate-200 px-3 py-2 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        />
        <span className="text-xs text-slate-500">{t('signup.tenantNameHint')}</span>
      </label>
      {turnstileEnabled && turnstileSiteKey !== undefined && (
        <Turnstile siteKey={turnstileSiteKey} onToken={onTurnstileToken} />
      )}
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-accent-600"
        />
        <span>
          {t('signup.consent.label')}{' '}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-700 underline hover:text-accent-800"
          >
            {t('signup.consent.policy')}
          </a>
          {t('signup.consent.glue')}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-700 underline hover:text-accent-800"
          >
            {t('signup.consent.terms')}
          </a>
          .
        </span>
      </label>
      {error !== null && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {pending ? t('signup.submitting') : t('signup.submit')}
      </button>
    </form>
  );
}
